/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import { Config } from "../config/Config";

/** What the product is called when nothing has said otherwise. */
export const UPSTREAM_PRODUCT_NAME = "Element Call";

/**
 * The name to show a user for this deployment.
 *
 * Three sources, most specific first:
 *
 *   1. `prenigma_branding.brand_name` from config.json — per deployment, so one
 *      build can serve Peitho at one host and Chain.Care at another. This block
 *      already exists and is already populated; nothing new is introduced here.
 *   2. `VITE_PRODUCT_NAME` — upstream's build-time mechanism, kept working.
 *   3. The upstream name.
 *
 * `Config.get()` THROWS when read before `Config.init()` resolves, and two of
 * the call sites are error paths (`RichError`, `utils/errors`) which are exactly
 * the places config may not have loaded. Reading it undefended would turn an
 * error message into a second error, so the throw is caught and treated as
 * "nothing more specific is known yet" rather than propagated.
 */
export function productName(): string {
  try {
    const brand = Config.get().prenigma_branding?.brand_name?.trim();
    if (brand) return brand;
  } catch {
    // Config not initialised yet — fall through to the build-time value.
  }
  return import.meta.env.VITE_PRODUCT_NAME || UPSTREAM_PRODUCT_NAME;
}
